import { AnyAction } from 'redux';
import { PaginatedResponse } from '@synergy/api';
import { defaultPageSize } from '@synergy/config';
import { unique } from '@synergy/utils';

import {
    BONUS_CONTACT_CHANGED,
    CREATE_CONTACTS_LOGINS_COMPLETED,
    DELETE_EMPLOYEE_SUCCESS,
    DELETE_USER_LOGIN_SUCCESS,
    EMAIL_EXISTS_WARNING,
    EMPLOYEE_PERMISSIONS_CHANGED,
    PATCH_CONTACTS_LOGINS_COMPLETED,
    RECEIVE_ORDER_CEILINGS,
    RECEIVE_USERS,
    REQUEST_DATA,
    RESET_USER,
    RESET_USER_MODAL,
    START_CREATE_CONTACTS_LOGINS,
    START_PATCH_CONTACTS_LOGINS,
    UPDATE_MAIN_CONTACT,
} from '../actions/contacts-logins-actions';
import { Contact } from '../models/contact';
import { LoginUser, isLoginUser } from '../../features/contacts-logins/models/login-user';

export interface ContactsLoginsReducerState {
    paginatedUsers: PaginatedResponse<Contact | LoginUser>;
    orderCeilings: number[];
    requestingUser: boolean;
    isExistingEmail: boolean;
    bonusContactCount: number;
}

const initialContactsLoginsReducerState: ContactsLoginsReducerState = {
    paginatedUsers: { items: [], page: 0, limit: defaultPageSize, total: 0 },
    orderCeilings: [],
    requestingUser: false,
    isExistingEmail: false,
    bonusContactCount: 0,
};

export const contactsLoginsReducer = (
    state: ContactsLoginsReducerState = initialContactsLoginsReducerState,
    action: AnyAction
): ContactsLoginsReducerState => {
    const { type, ...rest } = action;
    switch (type) {
        case REQUEST_DATA: {
            return {
                ...state,
                requestingUser: true,
                isExistingEmail: false,
            };
        }

        case START_CREATE_CONTACTS_LOGINS:
        case START_PATCH_CONTACTS_LOGINS: {
            return {
                ...state,
                isExistingEmail: false,
            };
        }

        case DELETE_EMPLOYEE_SUCCESS: {
            const { userId, assignTopicsToContactId, assignMainContactId, assignBonusContactId } = rest;
            let nextItems = [...state.paginatedUsers.items];

            const removedUser = nextItems.find(user => user.id === userId);
            if (!removedUser) {
                return state;
            }
            nextItems = nextItems.filter(user => user !== removedUser);

            if (assignTopicsToContactId) {
                const reassignTopicsIndex = nextItems.findIndex(user => user.id === assignTopicsToContactId);
                if (reassignTopicsIndex !== -1) {
                    nextItems[reassignTopicsIndex] = {
                        ...nextItems[reassignTopicsIndex],
                        // Join topics and filter unique
                        topics: unique(
                            [...(nextItems[reassignTopicsIndex].topics ?? []), ...(removedUser.topics ?? [])],
                            topic => topic.id
                        ),
                    };
                }
            }

            if (assignMainContactId) {
                const reassignMainContactIndex = nextItems.findIndex(user => user.id === assignMainContactId);
                if (reassignMainContactIndex !== -1) {
                    nextItems[reassignMainContactIndex] = {
                        ...nextItems[reassignMainContactIndex],
                        isMainContact: true,
                    };
                }
            }

            if (assignBonusContactId) {
                const reassignBonusContactIndex = nextItems.findIndex(user => user.id === assignBonusContactId);
                if (reassignBonusContactIndex !== -1) {
                    nextItems[reassignBonusContactIndex] = {
                        ...nextItems[reassignBonusContactIndex],
                        isBonusContact: true,
                    };
                }
            }

            return {
                ...state,
                paginatedUsers: {
                    ...state.paginatedUsers,
                    items: nextItems,
                },
            };
        }

        case UPDATE_MAIN_CONTACT: {
            const paginatedUsers = { ...state.paginatedUsers };

            paginatedUsers.items = paginatedUsers.items.map(item => {
                return {
                    ...item,
                    isMainContact: item.id === rest['mainContactId'],
                };
            });

            return {
                ...state,
                paginatedUsers: paginatedUsers,
            };
        }

        case DELETE_USER_LOGIN_SUCCESS: {
            const { userId, assignTopicsToContactId } = rest;
            let nextItems = [...state.paginatedUsers.items];

            const removedUserLogin = nextItems.find(user => user.id === userId);
            if (!removedUserLogin) {
                return state;
            }
            nextItems = nextItems.map(contact => {
                if (contact.id === userId) {
                    return { ...contact, user: null, topics: [] };
                } else if (contact.id === assignTopicsToContactId && isLoginUser(contact)) {
                    return {
                        ...contact,
                        // Join topics and filter unique
                        topics: unique(
                            [...(contact.topics ?? []), ...(removedUserLogin.topics ?? [])],
                            topic => topic.id
                        ),
                    };
                }
                return contact;
            });

            return {
                ...state,
                paginatedUsers: {
                    ...state.paginatedUsers,
                    items: nextItems,
                },
            };
        }

        case EMAIL_EXISTS_WARNING: {
            return {
                ...state,
                isExistingEmail: true,
                requestingUser: false,
            };
        }

        case PATCH_CONTACTS_LOGINS_COMPLETED: {
            const duplicatedPaginatedUsers = { ...state.paginatedUsers };
            const loginUser = duplicatedPaginatedUsers.items.find(user => user.id === rest['user'].id);

            if (isLoginUser(loginUser)) {
                const duplicatedLoginUser = { ...loginUser };
                duplicatedLoginUser.user = rest['user'];

                const index = duplicatedPaginatedUsers.items.findIndex(data => data.id === duplicatedLoginUser.id);
                if (index > -1) {
                    duplicatedPaginatedUsers.items[index] = loginUser;
                }
            }

            return {
                ...state,
                requestingUser: false,
                paginatedUsers: {
                    ...duplicatedPaginatedUsers,
                },
            };
        }

        case CREATE_CONTACTS_LOGINS_COMPLETED: {
            const paginatedUsers = { ...state.paginatedUsers };
            paginatedUsers.total++;

            return {
                ...state,
                paginatedUsers: {
                    ...paginatedUsers,
                },
            };
        }

        case RECEIVE_USERS: {
            return {
                ...state,
                paginatedUsers: rest['paginatedUsers'],
                requestingUser: false,
                bonusContactCount: rest['bonusContactCount'],
            };
        }

        case RECEIVE_ORDER_CEILINGS: {
            return {
                ...state,
                orderCeilings: rest['orderCeilings'],
            };
        }

        case RESET_USER_MODAL: {
            return {
                ...state,
                isExistingEmail: false,
            };
        }

        case RESET_USER: {
            return {
                ...state,
                paginatedUsers: initialContactsLoginsReducerState.paginatedUsers,
            };
        }

        case EMPLOYEE_PERMISSIONS_CHANGED: {
            const indexToUpdate = state.paginatedUsers.items.findIndex(user => user.id === rest['user'].id);

            if (indexToUpdate === -1) {
                return state;
            }

            return {
                ...state,
                paginatedUsers: {
                    ...state.paginatedUsers,
                    items: state.paginatedUsers.items.map((item, index) =>
                        index === indexToUpdate ? { ...item, permissions: rest['permissions'] } : item
                    ),
                },
            };
        }

        case BONUS_CONTACT_CHANGED: {
            const indexToUpdate = state.paginatedUsers.items.findIndex(user => user.id === rest['user'].id);

            if (indexToUpdate === -1) {
                return state;
            }
            const isBonusContact = rest['isBonusContact'];

            return {
                ...state,
                paginatedUsers: {
                    ...state.paginatedUsers,
                    items: state.paginatedUsers.items.map((item, index) =>
                        index === indexToUpdate ? { ...item, isBonusContact } : item
                    ),
                },
                bonusContactCount: isBonusContact ? state.bonusContactCount + 1 : state.bonusContactCount - 1,
            };
        }

        default: {
            return state;
        }
    }
};
